import { useState, useContext } from "react";
import { Form, Button, Spinner, Row, Col, OverlayTrigger, Tooltip, Modal } from "react-bootstrap";
import { UserInfo } from "../helper/Context.js";
import * as Yup from "yup";
import { Formik, FieldArray } from "formik";
import "./DoiRequestModal.css";

import { APIContact } from "../helper/APIFunctions.js";

const validationSchema = Yup.object().shape({
  email: Yup.string().required("Required").email("Invalid Format"),
  title: Yup.string().required("Required"),
  description: Yup.string().required("Required"),
  authors: Yup.array().of(
    Yup.object().shape({
      firstName: Yup.string().required("Required"),
      lastName: Yup.string().required("Required"),
      orcid: Yup.string(), // No validation for ORCID
    })
  ),
  link: Yup.string().url("Invalid URL").required("Required"),
});

const DoiRequestModal = ({ show, onHide, visualUrl }) => {
  const { userInfo } = useContext(UserInfo);
  const [status, setStatus] = useState({});

  return (
    <Modal show={show} onHide={onHide} size="lg" className="doi-request-modal">
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>Digital Object Identifier (DOI) Request</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {status.error ? <p className="text-danger">{status.error}</p> : null}
        {status.success ? (
          <div className="text-center">
            <h4>Your DOI request has been received.</h4>
            <p>You can expect a response within a few business days.</p>
            <Button onClick={onHide} className="mt-3">Close</Button>
          </div>
        ) : (
          <Formik
            initialValues={{
              email: (userInfo || {}).email || "",
              title: "",
              description: "",
              authors: [{ firstName: "", lastName: "", orcid: "" }],
              link: visualUrl || "",
            }}
            validationSchema={validationSchema}
            onSubmit={(values, actions) => {
              const contentBody = `
                Title: ${values.title}
                Description: ${values.description}
                Authors: ${values.authors.map(author => `${author.firstName} ${author.lastName} (ORCID: ${author.orcid})`).join(", ")}
                Link: ${values.link}
              `;

              const emailContent = {
                email: values.email,
                comment: contentBody,
                header: `DOI Request: ${values.title}`,
              };

              APIContact(
                emailContent,
                () => {
                  actions.setSubmitting(false);
                  setStatus({ success: true });
                },
                (err) => {
                  actions.setSubmitting(false);
                  setStatus({ error: err });
                }
              );
            }}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              touched,
              errors,
              isSubmitting,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <p>A Digital Object Identifier (DOI) is a unique, permanent identifier used to reference and access digital content. Snorkle is a member of <a href="https://www.crossref.org/about" target="_blank" rel="noopener noreferrer" style={{ color: "gray", textDecoration: "none" }}>Crossref</a>, through which we issue DOIs. <b>The following fields are required for DOI requests:</b></p>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="my-3">
                      <Form.Label>Title</Form.Label>
                      <Form.Control
                        name="title"
                        value={values.title}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={isSubmitting}
                        isInvalid={touched.title && errors.title}
                      />
                      <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="my-3">
                      <Form.Label>Your Contact Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={isSubmitting}
                        isInvalid={touched.email && errors.email}
                      />
                      <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group className="my-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        name="description"
                        placeholder="At least references are required; additional metadata is helpful."
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={isSubmitting}
                        isInvalid={touched.description && errors.description}
                        rows={4}
                      />
                      <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="my-3">
                  <Form.Label>Link to Visual</Form.Label>
                  <Form.Control
                    name="link"
                    value={values.link}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting || !!visualUrl}
                    isInvalid={touched.link && errors.link}
                  />
                  <Form.Control.Feedback type="invalid">{errors.link}</Form.Control.Feedback>
                </Form.Group>
                <h5>Authors</h5>
                <FieldArray
                  name="authors"
                  render={(arrayHelpers) => (
                    <div>
                      {values.authors.map((author, index) => (
                        <Row key={index} className="align-items-center my-2">
                          <Col><Form.Control placeholder="First Name" name={`authors.${index}.firstName`} value={author.firstName} onChange={handleChange} onBlur={handleBlur} /></Col>
                          <Col><Form.Control placeholder="Last Name" name={`authors.${index}.lastName`} value={author.lastName} onChange={handleChange} onBlur={handleBlur} /></Col>
                          <Col><Form.Control placeholder="ORCID (Optional)" name={`authors.${index}.orcid`} value={author.orcid} onChange={handleChange} onBlur={handleBlur} /></Col>
                          {index > 0 && <Col xs="auto"><Button variant="danger" onClick={() => arrayHelpers.remove(index)}>Remove</Button></Col>}
                        </Row>
                      ))}
                      <Button className="mt-2" onClick={() => arrayHelpers.push({ firstName: "", lastName: "", orcid: "" })}>
                        Add Author
                      </Button>
                    </div>
                  )}
                />
                <div className="d-flex justify-content-end mt-4">
                    <Button variant="secondary" onClick={onHide} className="me-2">Cancel</Button>
                    <Button type="submit" variant="primary" disabled={isSubmitting || status.success}>
                        {isSubmitting ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : "Submit"}
                    </Button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default DoiRequestModal; 